import _ from 'underscore';

import {MenuChoicesOptions} from 'components/SelectionContextMenuHelpers';
import {Strand} from 'constants/MolbioConstants';
import antibodyAnalysisStore from 'molbio/antibody-engineering/antibodyAnalysis';
import {
  CDRAnnotation,
  CDRDefinition,
  ChainType,
} from 'molbio/antibody-engineering/AntibodyEngineeringConstants';
import * as sequtil from 'molbio/SeqUtil';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {ANTIBODY_CHAIN_GREEK_NAMES} from 'util/UnicodeSymbols';

import AaSequenceAnnotationsView, {
  AaSequenceAnnotationsViewOptions,
  AaSequenceAnnotationView,
  AaSequenceAnnotationViewAttributes,
} from './AaSequenceAnnotationsView';
import AaSequenceStore from './AaSequenceStore';

// Lighter version of our primary button color, designed for CDR regions
export const CDR_COLOR = '#8AB6FF';
export const FR_COLOR = GRAY_LIGHT;

export function formatRegionName(name: string, chainType: ChainType): string {
  if (chainType === ChainType.HEAVY) {
    return `H ${name}`;
  } else if ([ChainType.KAPPA, ChainType.LAMBDA].includes(chainType)) {
    return `L ${name}${ANTIBODY_CHAIN_GREEK_NAMES[chainType]}`;
  } else {
    return `${name}${ANTIBODY_CHAIN_GREEK_NAMES[chainType]}`;
  }
}

export class CDRAnnotationView extends AaSequenceAnnotationView {
  override initialize(attributes: AaSequenceAnnotationViewAttributes, options: {syncToStore: boolean}): void {
    super.initialize(attributes, {...options, syncToStore: false});
  }
  override strand(): Strand {
    return 0;
  }
  override color(): string {
    return this.get('name').indexOf('FR') >= 0 ? FR_COLOR : CDR_COLOR;
  }
}

interface AaSequenceCDROptions extends AaSequenceAnnotationsViewOptions {
  aminoAcids?: string;
}

export class AaSequenceCDRView extends AaSequenceAnnotationsView {
  // Only used to determine if aminoAcids have changed when the AA sequence store changes, as we don't have a
  // way of only subscribing to aminoAcid changes
  cachedAminoAcids: string;
  cachedAnnotations: Array<CDRAnnotation>;
  override option!: vo.VisibilityOption;

  static override initClass(): void {
    this.prototype.setting = vo.ANTIBODY_CDR_ANNOTATIONS.name;
  }

  constructor(options: AaSequenceCDROptions) {
    super(options);
    this.className = 'antibodyCDRAnnotations';

    // In linear maps, the model is a map state and has an AA sequence. In alignments, we are given the amino acids
    // directly
    this.cachedAminoAcids = options.model.protein
      ? options.model.protein().aminoAcids()
      : options.aminoAcids!;
    this.cachedAnnotations = [];
    this._fetchCDRAnnotations();
  }

  override shouldShow(): boolean {
    return this.option.get('visible');
  }

  override boxHeight(): number {
    return this.cachedAnnotations.length > 0 ? super.boxHeight() : 0;
  }

  /**
   * Normal protein annotations sync themselves to the protein store, but CDR annotations aren't real
   * annotations, and should not
   */
  override shouldSyncToStore(): boolean {
    return false;
  }

  _getAminoAcids(): string {
    return this.model.protein().aminoAcids();
  }

  /**
   * Assumes either bases or definition have changed, and fetches from the store
   */
  async _fetchCDRAnnotations(): Promise<void> {
    if (!this.option.get('visible')) {
      return;
    }
    const [chains] = await antibodyAnalysisStore.getCDRAnnotations(
      [this._getAminoAcids()],
      (this.option.getSetting('CDRDefinition')!.get('value') as string).toLowerCase() as CDRDefinition
    );
    const formattedRegions = chains?.map((chain) =>
      chain.regions.map((reg) => ({...reg, name: formatRegionName(reg.name, chain.chainType)}))
    );
    // eslint-disable-next-line no-restricted-syntax
    this.cachedAnnotations = formattedRegions ? _.flatten(formattedRegions) : [];
    this.update();
  }

  _addAminoAcidsListener(): void {
    AaSequenceStore.addChangeListener(() => {
      if (this._getAminoAcids() !== this.cachedAminoAcids) {
        this.cachedAminoAcids = this._getAminoAcids();
        this._fetchCDRAnnotations();
      }
    });
  }

  _addVisibilityOptionsListener(): void {
    this.listenTo(this.option.get('settings'), 'change:value', this._fetchCDRAnnotations.bind(this));
  }

  /**
   * CDR annotations are a function of a protein's aminoAcids and the CDR definition the user picks, so
   * fetch new annotations when either of those change.
   */
  override _registerAnnotationListeners(): void {
    this._addAminoAcidsListener();
    this._addVisibilityOptionsListener();
  }

  /**
   * Returns cached annotations, and is not responsible for triggering fetching updated annotations
   */
  override getData(sliceStart: number, sliceEnd: number): Array<CDRAnnotationView> {
    return this.cachedAnnotations
      .filter((annotation) => sequtil.isRangeOverlap(sliceStart, sliceEnd, annotation.start, annotation.end))
      .map((annotation) => new CDRAnnotationView(annotation));
  }

  /**
   * Override normal annotation context menu, as CDR annotations aren't editable/removable
   */
  override contextMenuData(): MenuChoicesOptions {
    return {};
  }

  /**
   * Override normal annotation double click behavior since CDR annotations don't appear in the annotation panel.
   */
  dblClick(): void {}
}
AaSequenceCDRView.initClass();

export default AaSequenceCDRView;
